
import React from 'react';
import TeacherLayout from "@/components/layout/teacher/teacherLayout";
import {sprintf} from "@/lib/utils/sprintf";
import {URLS} from "@/lib/urls";
import {Link, useParams} from "react-router-dom";
import {useApiSearchTeacherStudentsDetail} from "@/api/userAcademy";
import RenderLoading from "@/components/render-if-loading";

import {
    Avatar,
    Card,
    Col,
    Descriptions,
    Row,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import {
    BookOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    StarFilled,
    TrophyOutlined,
    UserOutlined,
} from "@ant-design/icons";
import {convertToJalaliWithFormat} from "@/lib/utils/date";
import RenderIf from "@/components/render-if";
import {UserCheck2Icon} from "lucide-react";

const {Title, Text} = Typography;

const statusMap = {
    taken: {label: "ثبت‌نام شده", color: "green"},
    awaiting: {label: "در انتظار تایید", color: "orange"},
    rejected: {label: "رد شده", color: "red"},
    accepted: {label: "تایید شده", color: "green"},
};

const absenceTypeMap = {
    late: {label: "تاخیر", color: "orange", icon: <ClockCircleOutlined/>},
    absent: {label: "غیبت", color: "red", icon: <CloseCircleOutlined/>},
    present: {label: "حضور", color: "green", icon: <CheckCircleOutlined/>},
};

const homeworkStatusMap = {
    accepted: {label: "تایید شده", color: "green"},
    rejected: {label: "رد شده", color: "red"},
    pending: {label: "در انتظار", color: "orange"},
};

const absenceColumns = [
    {
        title: "تاریخ غیبت",
        dataIndex: "date",
        key: "date",
        render: (date) => (
            <Space>
                <CalendarOutlined style={{color: "#1677ff"}}/>
                <Text className="dark:!text-gray-200">{convertToJalaliWithFormat(date)}</Text>
            </Space>
        ),
    },
    {
        title: "نوع",
        dataIndex: "type",
        key: "type",
        render: (type) => {
            const info = absenceTypeMap[type] ?? {label: type, color: "default", icon: null};
            return <Tag color={info.color} icon={info.icon}>{info.label}</Tag>;
        },
    },
];

const homeworkColumns = [
    {
        title: "عنوان تکلیف",
        key: "title",
        render: (_, record) => (
            <Space direction="vertical" size={0}>
                <Text strong className="dark:!text-gray-100">{record.homework.title}</Text>
                <Text type="secondary" className="dark:!text-gray-400" style={{fontSize: 12}}>
                    {record.homework.description}
                </Text>
            </Space>
        ),
    },
    {
        title: "تاریخ",
        key: "date",
        render: (_, record) => (
            <Space>
                <CalendarOutlined style={{color: "#1677ff"}}/>
                <Text className="dark:!text-gray-200">{convertToJalaliWithFormat(record.homework.date)}</Text>
            </Space>
        ),
    },
    {
        title: "پاسخ دانش آموز به تکلیف",
        key: "report",
        render: (_, record) => (
            <Text type="secondary" className="dark:!text-gray-300">{record?.report}</Text>
        ),
    },
    {
        title: "نمره / حداکثر",
        key: "score",
        render: (_, record) => (
            <Space>
                <StarFilled style={{color: "#faad14"}}/>
                <Text
                    strong
                    style={{color: record.score >= record.homework.score / 2 ? "#52c41a" : "#ff4d4f"}}
                >
                    {record.score}
                </Text>
                <Text type="secondary" className="dark:!text-gray-400">/ {record.homework.score}</Text>
            </Space>
        ),
    },
    {
        title: "وضعیت",
        dataIndex: "status",
        key: "status",
        render: (status) => {
            const info = homeworkStatusMap[status] ?? {label: status, color: "default"};
            return <Tag color={info.color}>{info.label}</Tag>;
        },
    },
    {
        title: "پاسخ شما",
        dataIndex: "advice",
        key: "advice",
        render: (advice) => advice ? (
            <Tooltip title={advice}>
                <Text type="secondary" className="dark:!text-gray-300">{advice}</Text>
            </Tooltip>
        ) : (
            <Text type="secondary" className="dark:!text-gray-500">—</Text>
        ),
    },
];

function TeacherShowStudentProfilePage() {
    const {id, studentId} = useParams();

    const {data: studentsDetail, isPending, isSuccess} = useApiSearchTeacherStudentsDetail({
        student_id: studentId,
        lesson_id: id,
    });

    if (!isSuccess) {
        return <Spin/>;
    }

    const statusConfig = {
        pending: {color: 'orange', label: 'در انتظار بررسی', icon: <ClockCircleOutlined/>},
        accepted: {color: 'green', label: 'تایید شده', icon: <CheckCircleOutlined/>},
        rejected: {color: 'red', label: 'رد شده', icon: <CloseCircleOutlined/>},
    };

    function formatDate(iso) {
        const d = new Date(iso);
        return d.toLocaleDateString('fa-IR', {year: 'numeric', month: 'long', day: 'numeric'});
    }

    function getScoreStyles(score, maxScore) {
        if (score > maxScore / 2) {
            return {
                bg: 'bg-green-50 dark:bg-green-950/40',
                text: 'text-green-700 dark:text-green-300',
                border: 'border-green-200 dark:border-green-800',
            };
        }

        if (score > maxScore / 4) {
            return {
                bg: 'bg-yellow-50 dark:bg-yellow-950/40',
                text: 'text-yellow-700 dark:text-yellow-300',
                border: 'border-yellow-200 dark:border-yellow-800',
            };
        }

        return {
            bg: 'bg-red-50 dark:bg-red-950/40',
            text: 'text-red-700 dark:text-red-300',
            border: 'border-red-200 dark:border-red-800',
        };
    }

    const data = studentsDetail?.data?.data;
    const {student_detail: student, lesson} = data;
    const fullName = `${student.firstname} ${student?.lastname}`;
    const examScore = data?.examscore ? parseFloat(data?.examscore ?? 0) : '';

    return (
        <TeacherLayout
            backTo={sprintf(URLS.teacher.lessons.students, {id: id})}
            pageTitle={'مشاهده پرونده دانش آموز'}
        >
            <RenderLoading loading={isPending} skeleton={true}>
                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={24}>
                        <Card
                            title={
                                <Space>
                                    <UserOutlined style={{color: "#1677ff"}}/>
                                    <span className="text-gray-800 dark:text-gray-100">اطلاعات شخصی</span>
                                </Space>
                            }
                            bordered={false}
                            className="rounded-2xl shadow-sm bg-white dark:bg-gray-900"
                        >
                            <Avatar
                                src={student?.avatar}
                                size={100}
                                icon={<UserOutlined/>}
                                className="flex md:absolute md:left-2 md:top-14 w-full mb-4"
                                style={{
                                    border: "4px solid #fff",
                                    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                                    flexShrink: 0,
                                    background: "#e6f4ff",
                                }}
                            />

                            <Descriptions column={1} size="small" colon={false}>
                                <Descriptions.Item label={<Text type="secondary">نام</Text>}>
                                    <Text strong className="dark:!text-gray-100">{student?.firstname}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label={<Text type="secondary">نام خانوادگی</Text>}>
                                    <Text strong className="dark:!text-gray-100">{student?.lastname}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label={<Text type="secondary">نام پدر</Text>}>
                                    <Text strong className="dark:!text-gray-100">{student?.father}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label={<Text type="secondary">جنسیت</Text>}>
                                    <Tag color={student.gender === "male" ? "blue" : "pink"}>
                                        {student.gender === "male" ? "مرد" : "زن"}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={<Text type="secondary">کد ملی</Text>}>
                                    <Text dir="ltr" className="dark:!text-gray-200">{student.nationalcode}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label={<Text type="secondary">موبایل</Text>}>
                                    <Text dir="ltr" className="dark:!text-gray-200">
                                        <a href={'tel:' + student.mobile}>
                                            <RenderIf condition={student?.mobile}>
                                                {student?.mobile}
                                            </RenderIf>
                                            <RenderIf condition={student?.parent_detail?.mobile}>
                                                {student?.parent_detail?.mobile}
                                            </RenderIf>
                                        </a>
                                    </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label={<Text type="secondary">ملیت</Text>}>
                                    <Space>
                                        <Text className="dark:!text-gray-200">{student?.nationality_detail?.fa_name}</Text>
                                        <Text type="secondary">({student?.nationality})</Text>
                                    </Space>
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>

                    <Col xs={24}>
                        <Card
                            title={
                                <Space>
                                    <BookOutlined style={{color: "#1677ff"}}/>
                                    <span className="text-gray-800 dark:text-gray-100">اطلاعات درس</span>
                                </Space>
                            }
                            bordered={false}
                            className="rounded-xl shadow-sm mx-4 mb-4 bg-white dark:bg-gray-900"
                        >
                            <Space direction="vertical" style={{width: "100%"}} size={0}>
                                <div className="bg-blue-50 dark:bg-blue-950/40 border-r-4 border-blue-500 rounded-lg p-3 mb-3">
                                    <Text type="secondary" className="dark:!text-gray-400" style={{fontSize: 12}}>
                                        عنوان درس
                                    </Text>
                                    <Text strong className="dark:!text-gray-100" style={{margin: 0, marginTop: 4, fontSize: 14, display: "block"}}>
                                        {lesson.title}
                                    </Text>
                                </div>

                                <Descriptions column={{xs: 1, sm: 2}} size="small" colon={false}>
                                    <Descriptions.Item label={<Text type="secondary">دوره</Text>}>
                                        <Text className="dark:!text-gray-200">{lesson.topic.group.title}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label={<Text type="secondary">کد درس</Text>}>
                                        <Text dir="ltr" className="dark:!text-gray-200">{lesson.code}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label={<Text type="secondary">نیمسال</Text>} span={2}>
                                        <Text className="dark:!text-gray-200">{lesson.semester.title}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label={<Text type="secondary">دوره آموزشی</Text>} span={2}>
                                        <Text className="dark:!text-gray-200">{lesson.topic.course.title}</Text>
                                    </Descriptions.Item>
                                </Descriptions>
                            </Space>
                        </Card>
                    </Col>

                    <Col xs={24} lg={24}>
                        <Card
                            bordered={false}
                            className="rounded-2xl shadow-sm mb-6 bg-white dark:bg-gray-900"
                        >
                            <Row gutter={[16, 16]}>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="نمره کل"
                                        value={data.score}
                                        prefix={<TrophyOutlined style={{color: "#1677ff"}}/>}
                                        valueStyle={{color: "#1677ff", fontWeight: 700}}
                                    />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="نمره آزمون"
                                        value={examScore}
                                        precision={2}
                                        prefix={<StarFilled style={{color: "#faad14"}}/>}
                                        valueStyle={{color: "#d48806", fontWeight: 700}}
                                    />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="تعداد غیبت"
                                        value={data?.absences?.length}
                                        prefix={<ExclamationCircleOutlined style={{color: "#ff4d4f"}}/>}
                                        valueStyle={{color: "#ff4d4f", fontWeight: 700}}
                                    />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="تکالیف ثبت شده"
                                        value={data?.homework_reports?.length}
                                        prefix={<FileTextOutlined style={{color: "#52c41a"}}/>}
                                        valueStyle={{color: "#52c41a", fontWeight: 700}}
                                    />
                                </Col>
                            </Row>
                        </Card>

                        <Card
                            title={
                                <Space>
                                    <ClockCircleOutlined style={{color: "#ff7a45"}}/>
                                    <span className="text-gray-800 dark:text-gray-100">سوابق حضور و غیاب</span>
                                    <Tag color="orange">{data?.absences?.length} مورد</Tag>
                                </Space>
                            }
                            bordered={false}
                            className="rounded-2xl shadow-sm mb-6 bg-white dark:bg-gray-900"
                        >
                            {data.absences.length > 0 ? (
                                <Table
                                    dataSource={data?.absences}
                                    columns={absenceColumns}
                                    rowKey="id"
                                    pagination={false}
                                    size="small"
                                    style={{direction: "rtl"}}
                                />
                            ) : (
                                <div className="text-center py-6">
                                    <CheckCircleOutlined className="text-green-500 text-3xl mb-2"/>
                                    <Text type="secondary" className="dark:!text-gray-400">
                                        هیچ غیبتی ثبت نشده است
                                    </Text>
                                </div>
                            )}
                        </Card>

                        <Card
                            title={
                                <Space>
                                    <FileTextOutlined style={{color: "#52c41a"}}/>
                                    <span className="text-gray-800 dark:text-gray-100">گزارش تکالیف</span>
                                    <Tag color="green">{data.homework_reports.length} تکلیف</Tag>
                                </Space>
                            }
                            bordered={false}
                            className="rounded-2xl shadow-sm mb-6 bg-white dark:bg-gray-900"
                        >
                            {data.homework_reports.length > 0 ? (
                                <>
                                    <Table
                                        className="hidden lg:flex flex-col"
                                        dataSource={data.homework_reports}
                                        columns={homeworkColumns}
                                        rowKey="id"
                                        pagination={false}
                                        size="small"
                                        style={{direction: "rtl"}}
                                    />

                                    <div className="flex flex-col lg:hidden">
                                        {data.homework_reports.map((homeworkReport, i) => {
                                            const cfg = statusConfig[homeworkReport.status];
                                            const scoreStyles = getScoreStyles(
                                                homeworkReport?.score,
                                                homeworkReport?.homework?.score
                                            );

                                            return (
                                                <div
                                                    key={homeworkReport.id + 'homeworkd-report' + i}
                                                    className="w-full py-3.5 px-5 cursor-pointer border-b border-gray-100 dark:border-gray-800 transition-all duration-200 relative border-r-4 border-r-transparent hover:bg-gray-50 dark:hover:bg-gray-800/70"
                                                >
                                                    <Link to={sprintf(URLS.teacher.homeworks.reportsEdit, {id: homeworkReport.id})}>
                                                        <div className="flex items-start gap-3">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex justify-between items-center mb-1">
                                                                    <RenderIf condition={!!homeworkReport?.score}>
                                                                        <span className={`${scoreStyles.bg} ${scoreStyles.text} ${scoreStyles.border} border rounded-full px-2 py-0.5 text-[11px] font-bold`}>
                                                                            نمره {homeworkReport?.score}/{homeworkReport?.homework?.score}
                                                                        </span>
                                                                    </RenderIf>
                                                                </div>

                                                                <div className="flex items-center gap-1.5 mb-1.5">
                                                                    <FileTextOutlined className="text-gray-500 dark:text-gray-400 text-[11px]"/>
                                                                    <span className="text-sm text-gray-700 dark:text-gray-200 font-medium truncate">
                                                                        عنوان تکلیف: {homeworkReport.homework?.title}
                                                                    </span>
                                                                </div>

                                                                <div className="border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-2 rounded-lg my-2 text-gray-700 dark:text-gray-200">
                                                                    <UserOutlined className="mx-1"/>
                                                                    {homeworkReport?.report}
                                                                </div>

                                                                <RenderIf condition={homeworkReport?.advice}>
                                                                    <div className="border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 p-2 rounded-lg my-2 text-gray-700 dark:text-gray-200">
                                                                        <div className="flex">
                                                                            <UserCheck2Icon className="mx-1 text-blue-600 dark:text-blue-400"/>
                                                                            <div>پاسخ شما:</div>
                                                                        </div>
                                                                        {homeworkReport?.advice}
                                                                    </div>
                                                                </RenderIf>

                                                                <div className="flex justify-between items-center">
                                                                    <Tag
                                                                        icon={cfg.icon}
                                                                        color={cfg.color}
                                                                        className="text-md m-0 rounded-full px-2 py-0"
                                                                    >
                                                                        {cfg.label}
                                                                    </Tag>
                                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                        {formatDate(homeworkReport.created_at)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Link>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-6">
                                    <FileTextOutlined className="text-gray-400 text-3xl mb-2"/>
                                    <Text type="secondary" className="dark:!text-gray-400">
                                        هیچ تکلیفی ثبت نشده است
                                    </Text>
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>
            </RenderLoading>
        </TeacherLayout>
    );
}

export default TeacherShowStudentProfilePage;
